import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { format } from "date-fns";
import { Modal } from "@mantine/core";
import { useSocket } from "../../contexts/SocketContext";
import {
  NotificationContextProvider,
  useNotificationContext,
} from "../../contexts/NotificationContext";
import { getUser } from "../../helpers/auth";
import {
  clearGlobalNotificationHandler,
  requestNotificationPermission,
  setGlobalNotificationHandler,
  showSocketNotification,
} from "../../helpers/notifications";
import EditAppointmentComponent from "../../pages/schedule/components/edit-appointment";

const formatAppointmentType = (type) => {
  if (!type) {
    return "N/A";
  }
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

const formatAppointmentDate = (startDate, startTime) => {
  const source = startTime || startDate;
  if (!source) {
    return "N/A";
  }
  return format(new Date(source), "MMM dd, yyyy");
};

const formatAppointmentTime = (startTime) => {
  if (!startTime) {
    return "N/A";
  }
  return format(new Date(startTime), "h:mm a");
};

const buildAppointmentBody = (appointmentData) => {
  const patientName = appointmentData.patientName || "Patient";
  const appointmentType = formatAppointmentType(appointmentData.appointmentType);
  const appointmentDate = formatAppointmentDate(
    appointmentData.startDate,
    appointmentData.startTime
  );
  const appointmentTime = formatAppointmentTime(appointmentData.startTime);
  const doctorLine = appointmentData.doctorName
    ? `Doctor: ${appointmentData.doctorName}`
    : "Doctor: Unassigned";

  return `${patientName} · ${appointmentType}\n${appointmentDate} at ${appointmentTime}\n${doctorLine}`;
};

const normalizeId = (value) =>
  value == null ? null : String(value).trim().toLowerCase();

const isForCurrentBranch = (appointmentData) => {
  const user = getUser();
  const userBranchId = normalizeId(user?.branchId);
  const appointmentBranchId = normalizeId(appointmentData.facilityBranchId);

  if (!userBranchId || !appointmentBranchId) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Appointment notification skipped: missing branch id", {
        userBranchId,
        appointmentBranchId,
      });
    }
    return false;
  }

  return userBranchId === appointmentBranchId;
};

const AppointmentSocketListener = () => {
  const { onAppointmentBooked, onAppointmentUpcoming } = useSocket();
  const { showNotificationContext } = useNotificationContext();
  const queryClient = useQueryClient();

  const invalidateAppointments = useCallback(() => {
    queryClient.invalidateQueries(["appointments"]);
  }, [queryClient]);

  useEffect(() => {
    setGlobalNotificationHandler((data) => {
      showNotificationContext({
        ...data,
        appointmentId: data.appointmentId || data.data?.appointmentId,
      });
    });
    return () => clearGlobalNotificationHandler();
  }, [showNotificationContext]);

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        requestNotificationPermission().catch(() => {});
      }
    }
  }, []);

  useEffect(() => {
    const handleBooked = (appointmentData) => {
      if (process.env.NODE_ENV === "development") {
        console.log("appointment-booked received", appointmentData);
      }

      if (!isForCurrentBranch(appointmentData)) {
        return;
      }

      invalidateAppointments();

      showSocketNotification({
        title: "New Appointment Booked",
        body: buildAppointmentBody(appointmentData),
        tag: "appointment-booked",
        variant: "success",
        appointmentId: appointmentData.id,
        data: {
          url: "/schedule",
          appointmentId: appointmentData.id,
        },
      });
    };

    const handleUpcoming = (appointmentData) => {
      if (process.env.NODE_ENV === "development") {
        console.log("appointment-upcoming received", appointmentData);
      }

      if (!isForCurrentBranch(appointmentData)) {
        return;
      }

      const minutesBefore = appointmentData.minutesBefore || 5;
      showSocketNotification({
        title: `Appointment in ${minutesBefore} minutes`,
        body: buildAppointmentBody(appointmentData),
        tag: `appointment-upcoming-${appointmentData.id}-${minutesBefore}`,
        variant: "danger",
        appointmentId: appointmentData.id,
        data: {
          url: "/schedule",
          appointmentId: appointmentData.id,
        },
      });
    };

    const cleanupBooked = onAppointmentBooked(handleBooked);
    const cleanupUpcoming = onAppointmentUpcoming(handleUpcoming);

    return () => {
      cleanupBooked();
      cleanupUpcoming();
    };
  }, [invalidateAppointments, onAppointmentBooked, onAppointmentUpcoming]);

  return null;
};

export const AppointmentNotificationHandler = () => {
  const queryClient = useQueryClient();
  const [appointmentModal, setAppointmentModal] = useState({
    isOpen: false,
    appointmentId: null,
  });

  const handleBellClick = useCallback((notificationData) => {
    const appointmentId =
      notificationData?.appointmentId || notificationData?.data?.appointmentId;
    if (appointmentId) {
      setAppointmentModal({ isOpen: true, appointmentId });
    }
  }, []);

  const handleCloseModal = () => {
    setAppointmentModal({ isOpen: false, appointmentId: null });
  };

  return (
    <NotificationContextProvider onBellClick={handleBellClick}>
      <AppointmentSocketListener />
      {appointmentModal.appointmentId && (
        <Modal
          opened={appointmentModal.isOpen}
          onClose={handleCloseModal}
          title="Appointment Details"
          size="lg"
          zIndex={1000}>
          <EditAppointmentComponent
            appointmentId={appointmentModal.appointmentId}
            onRefresh={() => queryClient.invalidateQueries(["appointments"])}
          />
        </Modal>
      )}
    </NotificationContextProvider>
  );
};
